import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Delete, Person } from '@mui/icons-material';
import { useCreatePlayer, useDeletePlayer, usePlayer, usePlayers, useSports, useUpdatePlayer } from '@/hooks/useApi';
import { useAdminMode } from '@/hooks/useManage';
import { CatalogList, ErrorState, LoadingState, useCatalogParams } from '@/components';
import { FormPage, KebabMenu } from '@/components/manage';
import { PlayerSportProfileForm } from '@/components/player-profiles';
import { useNotification } from '@/contexts/NotificationContext';
import { getApiErrorMessage } from '@/utils/apiError';
import type { PlayerGender, PlayerProfile, SportType } from '@/api/generated/model';
import type { Player } from '@/types';

const genderLabel: Record<string, string> = { male: 'Мужской', female: 'Женский' };

export const PlayersPage: React.FC = () => {
  const { q, page } = useCatalogParams();
  const { create } = useAdminMode();
  const { data, isLoading, error, refetch } = usePlayers({ page, limit: 20, q });

  return (
    <CatalogList
      title="Игроки"
      icon={<Person color="primary" />}
      isLoading={isLoading}
      error={error}
      onRetry={refetch}
      totalPages={data?.totalPages}
      createHref={create.player ? '/players/new' : undefined}
      emptyText="Игроков пока нет"
      rows={(data?.data ?? []).map((player) => ({
        id: player.id,
        title: [player.firstName, player.lastName].filter(Boolean).join(' '),
        muted: !player.active,
        href: `/players/${player.id}`,
        subtitle: [player.gender ? genderLabel[player.gender] : null, player.birthDate ? `д.р. ${player.birthDate.slice(0, 10)}` : null]
          .filter(Boolean)
          .join(' · ') || undefined,
      }))}
    />
  );
};

type ProfileRow = { sportType: SportType | ''; profile: Record<string, unknown> };
type PlayerWithProfiles = Player & { profiles?: PlayerProfile[] | null };

const PlayerFormInner: React.FC<{ player?: PlayerWithProfiles }> = ({ player }) => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotification();
  const { can } = useAdminMode();
  const { data: sports } = useSports();
  const [form, setForm] = useState({
    firstName: player?.firstName ?? '',
    lastName: player?.lastName ?? '',
    gender: player?.gender ?? '',
    birthDate: player?.birthDate?.slice(0, 10) ?? '',
    active: player?.active ?? true,
  });
  const [profiles, setProfiles] = useState<ProfileRow[]>(
    (player?.profiles ?? []).map((p) => ({
      sportType: p.sportType,
      profile: p.profile && typeof p.profile === 'object' ? (p.profile as Record<string, unknown>) : {},
    })),
  );
  const [dirty, setDirty] = useState(false);
  const createPlayer = useCreatePlayer();
  const updatePlayer = useUpdatePlayer();
  const deletePlayer = useDeletePlayer();

  const patch = (next: Partial<typeof form>) => {
    setForm((prev) => ({ ...prev, ...next }));
    setDirty(true);
  };
  const patchProfile = (index: number, next: Partial<ProfileRow>) => {
    setProfiles((prev) => prev.map((p, i) => (i === index ? { ...p, ...next } : p)));
    setDirty(true);
  };

  const submit = async () => {
    const payload = {
      firstName: form.firstName,
      lastName: form.lastName || null,
      gender: (form.gender as PlayerGender) || null,
      birthDate: form.birthDate || null,
      active: form.active,
      profiles: profiles
        .filter((p): p is ProfileRow & { sportType: SportType } => Boolean(p.sportType))
        .map((p) => ({ sportType: p.sportType, profile: Object.keys(p.profile).length ? p.profile : null })),
    };
    try {
      const saved = player
        ? await updatePlayer.mutateAsync({ id: player.id, data: payload })
        : await createPlayer.mutateAsync(payload);
      setDirty(false);
      showSuccess('Игрок сохранён');
      navigate(`/players/${saved.id}`, { replace: true });
    } catch (err) {
      showError(getApiErrorMessage(err, 'Не удалось сохранить игрока.'));
    }
  };

  return (
    <FormPage
      title={player ? 'Игрок' : 'Новый игрок'}
      onSubmit={() => void submit()}
      saving={createPlayer.isPending || updatePlayer.isPending}
      submitDisabled={!form.firstName || (player ? !can(player.permissions?.update) : false)}
      dirty={dirty}
      closeTo={player ? `/players/${player.id}` : '/players'}
      headerActions={
        player && (
          <KebabMenu
            items={[
              {
                id: 'delete',
                label: 'Удалить',
                icon: <Delete fontSize="small" />,
                danger: true,
                hidden: !can(player.permissions?.delete),
                confirm: { title: 'Удалить игрока?' },
                onClick: async () => {
                  await deletePlayer.mutateAsync(player.id);
                  navigate('/players', { replace: true });
                },
              },
            ]}
          />
        )
      }
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <TextField label="Имя" required fullWidth autoComplete="given-name" value={form.firstName} onChange={(e) => patch({ firstName: e.target.value })} />
        <TextField label="Фамилия" fullWidth autoComplete="family-name" value={form.lastName} onChange={(e) => patch({ lastName: e.target.value })} />
        <FormControl fullWidth>
          <InputLabel>Пол</InputLabel>
          <Select value={form.gender} label="Пол" onChange={(e) => patch({ gender: e.target.value })}>
            <MenuItem value="">
              <em>Не указан</em>
            </MenuItem>
            <MenuItem value="male">Мужской</MenuItem>
            <MenuItem value="female">Женский</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="Дата рождения"
          type="date"
          fullWidth
          autoComplete="bday"
          value={form.birthDate}
          onChange={(e) => patch({ birthDate: e.target.value })}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="h5" sx={{ flex: 1 }}>
              Профили по видам спорта
            </Typography>
            <Button
              onClick={() => {
                setProfiles((prev) => [...prev, { sportType: '', profile: {} }]);
                setDirty(true);
              }}
            >
              Добавить
            </Button>
          </Box>
          <Stack spacing={1.5}>
            {profiles.map((profile, index) => (
              <Paper key={index} variant="outlined" sx={{ p: 1.5, bgcolor: 'inherit' }}>
                <FormControl fullWidth sx={{ mb: 1 }}>
                  <InputLabel>Вид спорта</InputLabel>
                  <Select
                    value={profile.sportType}
                    label="Вид спорта"
                    onChange={(e) => patchProfile(index, { sportType: e.target.value as SportType, profile: {} })}
                  >
                    {(sports ?? []).map((sport) => (
                      <MenuItem key={sport.code} value={sport.code}>
                        {sport.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {profile.sportType && (
                  <PlayerSportProfileForm
                    sportType={profile.sportType}
                    value={profile.profile}
                    onChange={(next) => patchProfile(index, { profile: next })}
                  />
                )}
                <Button
                  color="error"
                  onClick={() => {
                    setProfiles((prev) => prev.filter((_, i) => i !== index));
                    setDirty(true);
                  }}
                >
                  Удалить профиль
                </Button>
              </Paper>
            ))}
          </Stack>
        </Box>
        <FormControlLabel
          sx={{ minHeight: 48 }}
          control={<Switch checked={form.active} onChange={(e) => patch({ active: e.target.checked })} />}
          label="Активен"
        />
      </Stack>
    </FormPage>
  );
};

export const PlayerFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: player, isLoading, error, refetch } = usePlayer(id ?? '', { queryKey: ['players', id ?? ''], enabled: Boolean(id) });
  if (!id) return <PlayerFormInner />;
  if (isLoading) return <LoadingState fullscreen />;
  if (error || !player) return <ErrorState message="Игрок не найден" onRetry={refetch} />;
  return <PlayerFormInner key={player.id} player={player as PlayerWithProfiles} />;
};
